// =============================================================================
// Dashboard "Now" sub-tab — current-state view (scoreboard + deep-dive modules).
//   R57: two-layer scoreboard + modules   R58: internal view (customer = Report)
//   R59: KPI tiles with trend arrows       R60: on-track at-a-glance status
//   R61: 12-month burn-up                  R62: KPI summary table
//   R69: progress against overall target
//
// Rendered inside the Dashboard page under the "Now" sub-tab. Receives insights as
// a prop from the Dashboard shell (no own fetch). Print/PDF lives on the shell header.
// =============================================================================
import { useNavigate } from 'react-router-dom'
import { TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from 'recharts'
import { Card, RagChip } from '../../components/ui'
import { fmtDate } from '../../lib/format'
import { useDashboardSlice, setSelectedKpi } from '../../store/usePageSlices'
import type { Insights, Meta, ScoreTile, TrendDir, KpiRow, CallRound } from '../../lib/types'

// KPI-table key → kpi_snapshots column. The DB column names differ from the KPI keys by design
// (see KPI_DESCRIPTORS in src/server/src/insights.js — this is the frontend half of that map).
const KPI_SNAP_COL: Record<string, keyof import('../../lib/types').KpiSnapshot> = {
  bb1_ordered: 'circuits_ordered', bb1_delivered: 'circuits_delivered', site_calls: 'site_calls_complete',
  llds_approved: 'llds_approved', hw_dispatched: 'hardware_dispatched', snow_raised: 'snow_crs_raised',
  migrations: 'migrated', hw_collected: 'hardware_collected',
}

// KPI Summary table + its trend chart, paired in one card with a shared KPI selection: the chart is
// the visual/expanded form of the table's Trend column, so clicking a table row drives the chart.
function KpiSummaryAndTrend({ insights }: { insights: Insights }) {
  const kpis = insights.kpi_table
  // Selected KPI persists in the page store so the chosen row/chart survives navigation.
  const selected = useDashboardSlice((s) => s.selectedKpi)
  const setSelected = setSelectedKpi
  const snapCol = KPI_SNAP_COL[selected]
  const planCurve = insights.plan_by_kpi[selected] || []
  const planByWeek = new Map(planCurve.map((p) => [p.week_start, p.cumulative_planned]))
  // Forecast curve (run-rate projection): dashed line from where actual ends, out to the horizon.
  const forecastCurve = insights.forecast_by_kpi[selected] || []
  const forecastByWeek = new Map(forecastCurve.map((p) => [p.week_start, p.cumulative_forecast]))

  // X-axis = the UNION of snapshot weeks (actual history), plan-curve weeks and forecast weeks (which
  // run into the future), sorted. Actual is null beyond the latest snapshot (line stops at "today");
  // planned continues its trajectory forward; forecast overlays actual-so-far against the full planned curve.
  const weeks = Array.from(new Set([
    ...insights.snapshots.map((s) => s.snapshot_date),
    ...planCurve.map((p) => p.week_start),
    ...forecastCurve.map((p) => p.week_start),
  ])).sort()
  const actualByWeek = new Map(insights.snapshots.map((s) => [s.snapshot_date, snapCol ? (s[snapCol] as number) : null]))
  const data = weeks.map((w) => ({
    week: w,
    actual: actualByWeek.has(w) ? actualByWeek.get(w) : null,
    planned: planByWeek.get(w) ?? null,
    forecast: forecastByWeek.get(w) ?? null,
  }))
  const label = kpis.find((k) => k.key === selected)?.label || selected

  return (
    <Card className="overflow-hidden p-4">
      <h3 className="mb-3 text-[13px] font-semibold text-ink">KPI Summary</h3>
      {/* Table left (compact), trend chart right. Stacks on narrow screens so the chart never squishes. */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        <div className="overflow-hidden rounded border border-line lg:col-span-2">
          <table className="w-full text-[12px]">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-muted">
              <tr><th className="px-2 py-2 text-left">KPI</th><th className="px-2 py-2 text-right">Tgt</th><th className="px-2 py-2 text-right">Act</th><th className="px-2 py-2 text-right">%</th><th className="px-2 py-2 text-center">Trend</th></tr>
            </thead>
            <tbody>
              {kpis.map((k: KpiRow) => {
                // planned is null when the KPI has no seeded plan curve — neutral target, no %, no colour.
                const hasPlan = k.planned != null && k.planned > 0
                const pct = hasPlan ? Math.round((k.actual / (k.planned as number)) * 100) : null
                const isSel = k.key === selected
                return (
                  <tr key={k.key} onClick={() => setSelected(k.key)}
                    className={`cursor-pointer border-t border-line/60 ${isSel ? 'bg-brand-600/10' : 'hover:bg-slate-50'}`}>
                    <td className="px-2 py-2 text-ink">{k.label}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-muted">{k.planned ?? '—'}</td>
                    <td className="px-2 py-2 text-right font-semibold tabular-nums" style={{ color: hasPlan && k.actual < (k.planned as number) ? 'var(--rag-amber)' : 'var(--ink)' }}>{k.actual}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-muted">{pct == null ? '—' : `${pct}%`}</td>
                    <td className="px-2 py-2"><div className="flex justify-center"><TrendIcon dir={k.trend} /></div></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Trend for the selected KPI — driven by the row selection on the left */}
        <div className="lg:col-span-3">
          <p className="mb-2 text-[11px] text-muted">Weekly actual vs planned vs forecast — <span className="font-medium text-ink">{label}</span> (click a row to change)</p>
          {data.length < 2 ? (
            <p className="py-10 text-center text-sm text-muted">Not enough history yet for a trend.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="week" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip labelFormatter={(l) => fmtDate(l)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="actual" name={label} stroke="#2563eb" strokeWidth={2} dot={false} connectNulls={false} animationDuration={250} />
                <Line type="monotone" dataKey="planned" name="Planned" stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="4 4" dot={false} connectNulls animationDuration={250} />
                {/* Forecast: run-rate projection from where actual ends to the horizon (amber dashed, matching the burn-up). */}
                <Line type="monotone" dataKey="forecast" name="Forecast" stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="2 2" dot={false} connectNulls animationDuration={250} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </Card>
  )
}

// Resolve each RAG breakdown item's colour + display label from the single-sourced RAG authority
// (RAG_BANDS in stages.js, via /api/meta). Breakdown items are keyed by the raw brag token
// ('Green'/'Blue'/…/'unscheduled'); ragBands maps each token → { label, color }.
function bragPresentation(meta: Meta) {
  const colorByToken: Record<string, string> = {}
  const displayByToken: Record<string, string> = {}
  Object.values(meta.ragBands).forEach((b) => { colorByToken[b.key] = b.color; displayByToken[b.key] = b.label })
  return { colorByToken, displayByToken }
}

function TrendIcon({ dir }: { dir: TrendDir }) {
  if (dir === 'up') return <TrendingUp className="h-3.5 w-3.5" />
  if (dir === 'down') return <TrendingDown className="h-3.5 w-3.5" />
  return <Minus className="h-3.5 w-3.5" />
}

function ScoreCard({ tile, onClick }: { tile: ScoreTile; onClick?: () => void }) {
  return (
    <div onClick={onClick} role={onClick ? 'button' : undefined}
      className={`bg-surface px-4 py-3 ${onClick ? 'cursor-pointer hover:bg-slate-50' : ''}`}
      style={{ borderLeft: `3px solid ${tile.accent || 'var(--brand-600)'}` }}>
      <div className="flex items-start justify-between">
        <p className="text-[11px] font-medium uppercase tracking-wide text-muted">{tile.label}</p>
        {/* Only render an icon when the tile has a trend concept; null = omit (no faked Minus). */}
        {tile.trend && <TrendIcon dir={tile.trend} />}
      </div>
      <p className="mt-1 text-[22px] font-semibold tabular-nums leading-tight text-ink">{tile.value}</p>
      {tile.sub && <p className="mt-0.5 text-[11px] text-muted">{tile.sub}</p>}
    </div>
  )
}

// T-Minus Stage Progression — one row per stage: a coloured fill bar (sites currently AT that stage)
// with a dashed "expected by now" marker, sites distributed across the delivery stages. Custom (not a
// charting library) so the expected marker + per-stage colour + labels read cleanly.
function StageProgression({ insights, meta }: { insights: Insights; meta: Meta }) {
  const { wip } = insights
  // Colour per stage/boundary, resolved from the single-sourced config (meta). Full stage
  // names are shown (the label column is widened to fit); the wip row already carries `label`.
  const colorByKey: Record<string, string> = {}
  meta.stages.forEach((s) => { colorByKey[s.key] = s.color })
  Object.values(meta.boundaryStates).forEach((b) => { colorByKey[b.key] = b.color })
  const rows = wip.filter((w) => w.count > 0 || w.expected > 0)
  // Scale bars to the biggest count/expected in view (not the whole estate) so short bars are visible.
  const maxVal = Math.max(1, ...rows.map((w) => Math.max(w.count, w.expected)))
  const pctOf = (n: number) => Math.min(100, (n / maxVal) * 100)

  return (
    <Card className="p-4">
      <div className="mb-3">
        <h3 className="text-[13px] font-semibold text-ink">T-Minus Stage Progression</h3>
        <p className="text-[11px] text-muted">Sites at each of the {meta.stages.length} delivery stages · <span className="border-b border-dashed border-slate-500">┊</span> = expected by now</p>
      </div>
      <div className="space-y-1">
        {rows.map((g) => {
          const fillPct = pctOf(g.count)
          const expPct = pctOf(g.expected)
          return (
            <div key={g.key} className="flex items-center gap-2">
              {/* Full stage name; truncates with a hover title as the overflow safety net. */}
              <span className="w-40 flex-shrink-0 truncate text-[11px] font-medium text-ink" title={g.label}>{g.label}</span>
              {/* Actual count (sites at stage) shown inside the coloured fill — tucked against its
                  right edge in white. Expected stays the dashed marker with no number. */}
              <div className="group relative h-5 flex-1 rounded bg-slate-100" title={`${g.count} at this stage · ${g.expected} expected by now`}>
                <div className="absolute inset-y-0 left-0 flex items-center justify-end rounded pr-1.5 transition-all" style={{ width: `${fillPct}%`, background: colorByKey[g.key] || '#94a3b8', opacity: 0.9 }}>
                  <span className="text-[11px] font-semibold tabular-nums text-white">{g.count}</span>
                </div>
                {g.expected > 0 && (
                  <div className="absolute inset-y-[-2px] border-l-2 border-dashed border-slate-500" style={{ left: `${expPct}%` }} title={`Expected by now: ${g.expected}`} />
                )}
              </div>
            </div>
          )
        })}
      </div>
      <p className="mt-2 text-[10px] text-muted">Number = sites at stage · dashed line = expected by now</p>
    </Card>
  )
}

export function DashboardNow({ insights, meta }: { insights: Insights; meta: Meta }) {
  const navigate = useNavigate()
  const { scoreboard, weekly, breakdowns, intro_calls } = insights
  const { colorByToken, displayByToken } = bragPresentation(meta)

  return (
    <div className="space-y-5">
      {/* Scoreboard (R59, R60) */}
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-card border border-line bg-line lg:grid-cols-3 xl:grid-cols-6">
        {scoreboard.map((t) => (
          <ScoreCard key={t.key} tile={t} />
        ))}
      </div>

      {/* Burn-up + BRAG donut (R61) */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="p-4 lg:col-span-2">
          <h3 className="mb-3 text-[13px] font-semibold text-ink">Performance Against Plan</h3>
          <p className="mb-2 text-[11px] text-muted">Cumulative migrations across the full programme — planned vs actual vs forecast. Migrations run ahead of “% Complete” (which counts sites fully closed out, through T+15).</p>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={weekly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              {/* Full-horizon series (~70+ weekly points). Matches the KPI-summary chart's x-axis style:
                  short MM-DD labels + Recharts auto-thinning, with a minTickGap to keep the denser
                  full-programme axis legible. */}
              <XAxis dataKey="week_start" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} minTickGap={24} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip labelFormatter={(l) => fmtDate(l)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="cumulative_planned" name="Planned" stroke="#94a3b8" fill="#f1f5f9" strokeDasharray="4 4" connectNulls dot={false} />
              {/* Actual stops at today (null in the future → line ends, no flat carry). */}
              <Area type="monotone" dataKey="cumulative_actual" name="Actual" stroke="#2563eb" fill="#dbeafe" connectNulls={false} dot={false} />
              {/* Forecast only from today onward (null in the past → no phantom diagonal over history). */}
              <Area type="monotone" dataKey="cumulative_forecast" name="Forecast" stroke="#f59e0b" fill="none" strokeDasharray="2 2" connectNulls={false} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-4">
          <h3 className="mb-3 text-[13px] font-semibold text-ink">Overall Status (RAG)</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={breakdowns.brag} dataKey="count" nameKey="label" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2}>
                {/* colour comes from the RAG authority (meta.ragBands); the neutral fallback is only for an
                    unrecognised token, which shouldn't occur since every emitted token is in ragBands. */}
                {breakdowns.brag.map((b) => <Cell key={b.label} fill={colorByToken[b.label] || '#cbd5e1'} />)}
              </Pie>
              <Tooltip formatter={(value, name) => [value, displayByToken[name as string] || name]} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-2 flex flex-wrap justify-center gap-3">
            {breakdowns.brag.map((b) => (
              <button key={b.label} onClick={() => navigate(`/?brag=${b.label}`)} className="flex items-center gap-1 text-[11px]">
                <RagChip rag={b.label} size="sm" label={`${displayByToken[b.label] || b.label} ${b.count}`} />
              </button>
            ))}
          </div>
        </Card>
      </div>

      {/* KPI Summary table + trend chart, paired with shared selection (R62: actual/target/trend) */}
      <KpiSummaryAndTrend insights={insights} />

      {/* T-Minus Stage Progression — sites per stage vs expected-by-now (per-site at-risk diagnosis
          lives on Intelligence; the Dashboard shows programme STATE). */}
      <StageProgression insights={insights} meta={meta} />

      {/* Intro Call Outcomes (R3/R44) — per-round Successful/Failed/Pending funnels + whole-estate
          coverage donut. Full-width. */}
      <IntroCallOutcomes intro={intro_calls} />

      {/* Site Size Reclassification — half-width on the left. */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <SizeClassificationMatrix cls={breakdowns.site_size_classification} />
      </div>
    </div>
  )
}

// Site-size reclassification matrix: original (rows) × confirmed (cols). Cells are heat-tinted by
// direction — sites that were re-sized UP (confirmed larger than original) are "bad" (scope grew,
// found late) and shaded red; DOWN is "good", shaded green; the diagonal (unchanged) is neutral.
// Intensity tracks how many bands were crossed. An "Unconf." column holds sites not yet confirmed
// (pending, not a discrepancy). A compact directional summary sits under the grid.
function SizeClassificationMatrix({ cls }: { cls: Insights['breakdowns']['site_size_classification'] }) {
  const { sizes, matrix, summary } = cls
  // Cell background: green (down/good) → neutral (unchanged) → red (up/bad), stronger with distance.
  const cellStyle = (count: number, direction: number, distance: number): React.CSSProperties => {
    if (count === 0) return {}
    if (direction === 0) return { backgroundColor: '#f1f5f9' } // unchanged — neutral
    const alpha = Math.min(0.14 + distance * 0.12, 0.4)
    const rgb = direction > 0 ? '220,38,38' /* red = upsized/bad */ : '22,163,74' /* green = downsized/good */
    return { backgroundColor: `rgba(${rgb},${alpha})` }
  }
  return (
    <Card className="p-4">
      <h3 className="mb-3 text-[13px] font-semibold text-ink">Site Size Reclassification</h3>
      {/* Two symmetric axes: the axis TITLES share one style, and the axis VALUE labels (size bands,
          whether row or column) share another. The value cells sit in a bordered block so the matrix
          reads as a distinct grid, separate from the axis labels. `axisTitle` / `axisVal` are the two
          shared styles applied on both dimensions. */}
      <div className="overflow-x-auto">
        {(() => {
          const axisTitle = 'text-[10px] font-semibold uppercase tracking-wide text-muted'
          const axisVal = 'text-[11px] font-medium text-ink'
          return (
            <table className="w-full table-fixed border-collapse">
              {/* Fixed layout so every value column (+ Unconfirmed) is the same width; the row-label
                  column takes a fixed slice. */}
              <colgroup>
                <col style={{ width: '18%' }} />
                {sizes.map((s) => <col key={s} style={{ width: `${72 / (sizes.length + 1)}%` }} />)}
                <col style={{ width: `${72 / (sizes.length + 1)}%` }} />
              </colgroup>
              <thead>
                {/* Confirmed axis title, spanning the value columns */}
                <tr>
                  <th />
                  <th colSpan={sizes.length} className={`pb-1 text-center ${axisTitle}`}>Confirmed size</th>
                  <th />
                </tr>
                <tr>
                  <th className={`p-1 text-left ${axisTitle}`}>Original</th>
                  {sizes.map((s) => <th key={s} className={`p-1 text-center ${axisVal}`}>{s}</th>)}
                  <th className={`p-1 text-center ${axisVal} text-muted`}>Unconfirmed</th>
                </tr>
              </thead>
              <tbody>
                {matrix.map((row) => (
                  <tr key={row.original}>
                    <td className={`p-1 ${axisVal}`}>{row.original}</td>
                    {row.confirmed.map((c) => (
                      <td key={c.size}
                        className="border border-line/60 p-1.5 text-center text-[11px] tabular-nums"
                        style={cellStyle(c.count, c.direction, c.distance)}>
                        {c.count || <span className="text-slate-300">·</span>}
                      </td>
                    ))}
                    {/* Unconf. is bordered too (so the grid isn't visually off-centre) but set apart:
                        dashed border + faint bg + muted italic value — "pending", not a reclass outcome. */}
                    <td className="border border-dashed border-line/60 bg-slate-50/50 p-1.5 text-center text-[11px] italic tabular-nums text-muted">
                      {row.unconfirmed || <span className="text-slate-300">·</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        })()}
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 border-t border-line pt-3 text-[11px]">
        <span className="flex items-center gap-1.5 text-red-600"><span className="h-2.5 w-2.5 rounded-sm bg-red-500/30" />Upsized <span className="font-semibold tabular-nums">{summary.upsized}</span></span>
        <span className="flex items-center gap-1.5 text-emerald-600"><span className="h-2.5 w-2.5 rounded-sm bg-emerald-500/30" />Downsized <span className="font-semibold tabular-nums">{summary.downsized}</span></span>
        <span className="flex items-center gap-1.5 text-muted"><span className="h-2.5 w-2.5 rounded-sm bg-slate-200" />Unchanged <span className="font-semibold tabular-nums text-ink">{summary.unchanged}</span></span>
        <span className="text-muted">Unconfirmed <span className="font-semibold tabular-nums text-ink">{summary.unconfirmed}</span></span>
      </div>
    </Card>
  )
}

// Intro-call outcomes — RT's three success/unsuccessful funnels (1st try / 2nd try / total), each a
// small pie with its own denominator, + a whole-estate coverage donut. Figures come straight from
// insights.intro_calls (no re-derivation). Success is an approximation (no per-attempt outcome flag).
// --- Shared semantic palette (consistent across all four charts) ---
//   GOOD  vivid green = best outcome (completed first time)
//   GOOD2 muted green  = good but slower (completed, but on the 2nd call — a "worse good")
//   WARN  amber        = recovered but not first-time-right (needed a 2nd call)
//   BAD   red          = genuine bad outcome (2nd-try unsuccessful, called-but-not-complete)
//   NA    grey         = not applicable (never started) — only ever means "not yet in play"
const CALL = { GOOD: '#16a34a', GOOD2: '#6ee7b7', WARN: '#f59e0b', BAD: '#dc2626', NA: '#cbd5e1' }
type Slice = { label: string; value: number; fill: string }
// One call round → the three ordered slices, with the shared semantic palette. Single-sources the
// success/failed/pending → label+colour mapping so all three pies read identically. `okFill` lets the
// 2nd-try round use the muted "worse good" green while keeping failed/pending consistent.
function roundSlices(r: CallRound, okFill: string = CALL.GOOD): Slice[] {
  return [
    { label: 'Successful', value: r.successful, fill: okFill },
    { label: 'Failed', value: r.failed, fill: CALL.BAD },
    { label: 'Pending', value: r.pending, fill: CALL.NA },
  ]
}
// Generic per-round pie: takes an ordered list of slices (2 or 3), each with its own semantic colour.
// Percentages are of the round's own denominator (sum of the slices).
function CallPie({ title, slices }: { title: string; slices: Slice[] }) {
  const total = slices.reduce((n, s) => n + s.value, 0)
  const data = slices.filter((s) => s.value > 0)
  const pct = (n: number) => (total ? Math.round((n / total) * 100) : 0)
  return (
    <div className="flex flex-col items-center">
      <h4 className="mb-1 text-[12px] font-medium text-ink">{title}</h4>
      {total === 0 ? (
        <div className="flex h-[130px] items-center justify-center text-[11px] text-muted">No calls yet</div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={130}>
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="label" cx="50%" cy="50%" outerRadius={52}>
                {data.map((d) => <Cell key={d.label} fill={d.fill} />)}
              </Pie>
              <Tooltip formatter={(value, name) => [value, name]} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-1 space-y-0.5 text-[11px]">
            {slices.map((l) => (
              <div key={l.label} className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: l.fill }} />
                <span className="text-ink">{l.label} <span className="font-semibold tabular-nums">{l.value}</span> ({pct(l.value)}%)</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
// Whole-estate composition donut — where every in-scope site sits (incl. the not-yet-started
// population the conversion pies exclude by using called-only denominators). Same semantic palette:
// completed = green (1st try full, 2nd try muted to distinguish within-donut); "Outstanding" (called,
// not complete — the total pie's failed+pending) = amber (attention, not definitive fail); "Not
// started" = grey (N/A).
function CompositionDonut({ comp }: { comp: Insights['intro_calls']['composition'] }) {
  const segments = [
    { label: 'Complete — 1st try', value: comp.first_try, fill: CALL.GOOD },
    { label: 'Complete — 2nd try', value: comp.second_try, fill: CALL.GOOD2 },
    { label: 'Outstanding', value: comp.outstanding, fill: CALL.WARN },
    { label: 'Not started', value: comp.not_started, fill: CALL.NA },
  ]
  const data = segments.filter((s) => s.value > 0)
  const total = segments.reduce((n, s) => n + s.value, 0)
  return (
    <div className="flex flex-col items-center">
      <h4 className="mb-1 text-[12px] font-medium text-ink">All sites — coverage</h4>
      {total === 0 ? (
        <div className="flex h-[130px] items-center justify-center text-[11px] text-muted">No sites in scope</div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={130}>
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="label" cx="50%" cy="50%" innerRadius={34} outerRadius={52} paddingAngle={2}>
                {data.map((s) => <Cell key={s.label} fill={s.fill} />)}
              </Pie>
              <Tooltip formatter={(value, name) => [value, name]} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-1 space-y-0.5 text-[11px]">
            {segments.map((s) => (
              <div key={s.label} className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: s.fill }} />
                <span className="text-ink">{s.label} <span className="font-semibold tabular-nums">{s.value}</span></span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
function IntroCallOutcomes({ intro }: { intro: Insights['intro_calls'] }) {
  return (
    <Card className="p-4">
      <h3 className="mb-3 text-[13px] font-semibold text-ink">Intro Call Outcomes</h3>
      {/* Every round uses the SAME three outcomes + colours: green success / red failed / grey pending. */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <CallPie title="Intro Calls — 1st try" slices={roundSlices(intro.first_try)} />
        <CallPie title="Intro Calls — 2nd try" slices={roundSlices(intro.second_try, CALL.GOOD2)} />
        <CallPie title="Total Calls Done" slices={roundSlices(intro.total)} />
        <CompositionDonut comp={intro.composition} />
      </div>
    </Card>
  )
}
